import clsx from 'clsx';

// Hooks
import { useToastsContext } from '@/utils/hooks/context/toast/useToastsContext';
import useToastsNotifications from '@/utils/hooks/swr/notifications/useToastsNotifications';
import type { mutateNotificationsType } from '@/utils/hooks/swr/useNotifications';
import useTranslation from '@/utils/hooks/translation/useTranslation';

// Utils
import { deleteApi } from '@/utils/client';

// Types
import type { NotificationType } from '@/utils/hooks/context/toast/types';

// Routes
import { DELETE_NOTIFICATION } from '@/lib/routes/apiEndpoints';

// Components
import Icon from '@/components/base/Icon';

type ButtonDeleteNotificationProps = {
    notification: NotificationType;
    mutateNotifications: mutateNotificationsType;
};

const ButtonDeleteNotification = ({
    notification,
    mutateNotifications
}: ButtonDeleteNotificationProps) => {
    const { t } = useTranslation();
    const { toastError } = useToastsContext();
    const { mutateToastsNotifications } = useToastsNotifications({
        revalidateOnMount: false,
        shouldRetryOnError: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });

    const deleteNotification = () => {
        try {
            // Launch delete request
            deleteApi(`${DELETE_NOTIFICATION}?notification_id=${notification.id}`);

            // Change global unread value
            if (!notification.read) {
                mutateToastsNotifications(
                    (prevState) => ({
                        ...prevState,
                        data: {
                            ...prevState.data,
                            total_unread: prevState?.data?.total_unread - 1
                        }
                    }),
                    false
                );
            }

            // Remove notification in cache + change unread value
            mutateNotifications((prevState) => {
                return prevState.map((page) => {
                    return {
                        ...page,
                        notifications: page.notifications.filter(
                            (item) => item.id !== notification.id
                        ),
                        total_count: page.total_count - 1,
                        total_unread: !notification.read ? page.total_unread - 1 : page.total_unread
                    };
                });
            }, false);
        } catch (e) {
            toastError({
                title: t('notifications:error_delete_notification')
            });
            mutateNotifications(undefined, true);
        }
    };

    return (
        <button
            onClick={deleteNotification}
            className={clsx(
                'flex items-center justify-center',
                'absolute -right-2.5 -top-2.5 h-5 w-5 rounded-full text-xs',
                'bg-white text-icoRed dark:bg-icoDarkMode-deepBlack',
                'opacity-0 transition-all group-hover:opacity-100'
            )}>
            <Icon icon="close" />
        </button>
    );
};

export default ButtonDeleteNotification;
